import {
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
  getPageNumber,
  getQueries,
  mapTargetBySelectors,
} from '@/features/shared/HOCs/withGTM';
import { OnDatalayerProps } from '@/features/shared/HOCs/withGTM.interface';
import qs from 'query-string';

enum Selectors {
  // Target
  Pagination = '.gtm-pagination-container a:not(.Mui-disabled)',
  Result = '.gtm-result .gtm-search-card',
  // Containers
  PaginationContainer = '.gtm-pagination-container',
  ResultContainer = '.gtm-result',
}

export const mapTarget = mapTargetBySelectors([Selectors.Pagination, Selectors.Result]);

export const onDatalayer = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const isResult = !!target?.closest(Selectors.ResultContainer);
  const isPagination = !!target?.closest(Selectors.PaginationContainer);
  const variantName = (target?.closest('[data-datalayer-variant]') as HTMLDivElement)?.dataset
    ?.datalayerVariant;

  const {
    q: searchQuery = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    keywords = '',
    page = 1,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortBy = 'most-relevant',
    size,
    ...filters
  } = qs.parse(window.location.search);

  const searchFilters = getQueries(filters);

  if (isPagination) {
    const pageNumber = getPageNumber({ target, page: Number(page) });

    return {
      event: 'clickPagination',
      eventCategory: 'Site Search',
      pageNumber,
      searchFilters,
      searchQuery,
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isResult) {
    const href = target?.href;

    return {
      event: 'clickResult',
      eventCategory: 'Site Search',
      pageNumber: page,
      searchFilters,
      searchQuery,
      href,
      ...(variantName && { variant_name: variantName }),
    };
  }

  return undefined;
};

export const onDatalayerCustomSort = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const {
    params: { sortOption, variantName = '' },
  } = target || {};

  const {
    q: searchQuery = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    keywords = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    page = 1,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortBy = 'most-relevant',
    size,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...filters
  } = qs.parse(window.location.search);

  const searchFilters = getQueries(filters);

  return {
    event: 'sortResults',
    eventCategory: 'Site Search',
    searchFilters,
    sortOption,
    searchQuery,
    ...(variantName && { variant_name: variantName }),
  };
};

export const onDatalayerCustomFilters = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const {
    params: { filters, variantName = '' },
  } = target || {};

  const { q: searchQuery = '' } = qs.parse(window.location.search);

  const searchFilters = getQueries(filters);

  return {
    event: 'clickFilters',
    eventCategory: 'Site Search',
    searchFilters,
    searchQuery,
    ...(variantName && { variant_name: variantName }),
  };
};

export const onDatalayerTriggerSearchResult = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const { totalResults, isRevision, indexNum } = target;

  const {
    q: searchQuery = '',
    keywords = '',
    page = 1,
    sortBy = 'most-relevant',
    size,
    ...filters
  } = qs.parse(window.location.search);

  const searchFiltersObject = getDataLayerSearchFiltersObject(filters as Record<string, string[]>);
  const searchFilters = getDataLayerSearchFilters(filters as Record<string, string[]>);

  return {
    event: 'trackEvent-searchResult',
    event_name: 'view_search_results',
    ...(!!searchQuery && { search_term: searchQuery }),
    num_search_results: totalResults,
    index_num: String(indexNum),
    is_revision: isRevision,
    ...(!!searchFilters && { search_filters: searchFilters }),
    num_search_filters: getDataLayerNumFilters(filters as Record<string, string[]>),
    ...searchFiltersObject,
    ...(!!filters.is_patner_only && { is_partner_only_filter: filters.is_patner_only[0] }),
  };
};

export const getPortalPageLoadSearchResult = ({ totalResults, isRevision, indexNum }: any) => {
  const {
    q: searchQuery = '',
    keywords = '',
    page = 1,
    sortBy = 'most-relevant',
    size,
    ...filters
  } = qs.parse(window.location.search);

  const searchFiltersObject = getDataLayerSearchFiltersObject(filters as Record<string, string[]>);
  const searchFilters = getDataLayerSearchFilters(filters as Record<string, string[]>);

  return {
    event: 'trackEventPortalPageLoad-searchResult',
    event_name: 'view_search_results',
    ...(!!searchQuery && { search_term: searchQuery }),
    num_search_results: totalResults,
    index_num: String(indexNum),
    is_revision: isRevision,
    ...(!!searchFilters && { search_filters: searchFilters }),
    num_search_filters: getDataLayerNumFilters(filters as Record<string, string[]>),
    ...searchFiltersObject,
    ...(!!filters.is_patner_only && { is_partner_only_filter: filters.is_patner_only[0] }),
  };
};
